import { Expression } from "@/components/Expressions/types";
import BubbleInvitationEditor from "./BubbleInvitationEditor";
import PopoverInvitationEditor from "./PopoverInvitationEditor";
import BannerInvitationEditor from "./BannerInvitationEditor";
import React from "react";

export interface Invitation {
  id?: number;
  siteId: number;
  type: "bubble" | "popover" | "banner";
  name: string;
  description: string;
  resetFrequency: InvitationResetFrequency;
  triggerExpression: Expression;
  routeToTeamId?: number;
}

export const InvitationResetFrequencies = [
  "Never",
  "Hourly",
  "Daily",
  "Weekly",
  "Monthly",
  "Yearly"
] as const;

export type InvitationResetFrequency =
  (typeof InvitationResetFrequencies)[number];

export interface BubbleInvitation extends Invitation {
  type: "bubble";
  messages: string[];
  messageDelaySeconds: number;
}

export interface PopoverInvitation extends Invitation {
  type: "popover";
  messages: string[];
  messageDelaySeconds: number;
  agentAvatarUserId?: number;
  ctaLabel: string;
}

export interface BannerInvitation extends Invitation {
  type: "banner";
  message: string;
  position: "top" | "bottom";
  ctaLabel: string;
}

export const invitationTypeDefs = [
  {
    label: "Bubble",
    value: "bubble",
    description: "A classic chat bubble that animates with one or more messages — great for proactive engagement.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: BubbleInvitation): void;
    }) => {
      return (
        <BubbleInvitationEditor
          invitation={invitation as BubbleInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  },
  {
    label: "Popover",
    value: "popover",
    description: "A floating card with agent avatar and a CTA button — great for proactive engagement on high-intent pages.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: PopoverInvitation): void;
    }) => {
      return (
        <PopoverInvitationEditor
          invitation={invitation as PopoverInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  },
  {
    label: "Banner",
    value: "banner",
    description: "A slim sticky bar at the top or bottom of the page — non-intrusive, high visibility.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: BannerInvitation): void;
    }) => {
      return (
        <BannerInvitationEditor
          invitation={invitation as BannerInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  }
];
